// My numbers (`/c/{slug}/cadence/my-numbers`; MEMBER-ENTRY, founder-approved 2026-10-21; spec 13
// §Screens #11): the numbers a member reports for the seats it holds. One call (`GET me/numbers`)
// lists, per seat ("Seat · Holder", KPI-FIX's label), each manual or imported KPI at the period
// due by its cadence, with its goal, a status chip ("Late", "Due Fri", "Entered"), a period
// picker over its recent periods (the cadence's own: days, weeks, months, quarters or years) and
// inline entry: type the number, Enter or Save writes `POST /observations` for the seat. A value
// that changes one already entered is a correction: the row asks why (the API refuses a holder's
// correction without its reason, and tells the supervisor when the period is past).
// The Home card ("Numbers due") is the same read: what is still late or due, newest due last.
import {
  Badge,
  Button,
  buttonVariants,
  EmptyState,
  HomeCard,
  HomeCardRow,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CalendarClockIcon, ClipboardListIcon, HashIcon } from "lucide-react";
import { type KeyboardEvent, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatMetricValue, formatTarget, seatHolderLabel } from "../format";
import { keys, myNumbersQuery } from "../queries";
import { Loading } from "../screen-parts";
import { toInputValue, toStoredValue, UNIT_HINT } from "../structure/labels";
import { ErrorNotice, FormError } from "../structure/parts";
import { localDay } from "../work/model";
import {
  dueChip,
  isCorrection,
  type MyNumberItem,
  type MySeat,
  outstanding,
  periodLabel,
  valueIn,
} from "./my-numbers-model";

const TONE = { late: "danger", due: "warning", done: "success" } as const;

/** The goal as the scorecard prints it ("≥ 20"); the item carries no range bound. */
const goalOf = (item: MyNumberItem, currency: string) =>
  formatTarget(
    {
      target: item.target,
      target_max: null,
      unit: item.unit,
      direction: item.direction,
    } as Parameters<typeof formatTarget>[0],
    currency,
  );

export function MyNumbersScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const numbers = useQuery(myNumbersQuery(api, company.id));
  const seats = numbers.data?.seats ?? [];
  const today = localDay(new Date());
  const open = outstanding(numbers.data).length;
  return (
    <div data-slot="my-numbers" className="flex flex-col gap-6">
      {numbers.data && seats.length ? (
        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          {open ? `${open} to enter` : "All in"}
          <InfoTip label="About my numbers">
            The numbers you report for the seats you hold. Pick an earlier period to correct one.
          </InfoTip>
        </p>
      ) : null}
      {numbers.isPending ? <Loading label="Loading your numbers" /> : null}
      {numbers.isError ? <ErrorNotice what="Your numbers" error={numbers.error} /> : null}
      {numbers.data && seats.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ClipboardListIcon}
          title="No numbers to report"
          description="None of your seats has a KPI entered by hand."
        />
      ) : null}
      {numbers.data
        ? seats.map((seat) => (
            <SeatNumbers
              key={seat.seat_id}
              api={api}
              company={company}
              seat={seat}
              currency={numbers.data.currency}
              today={today}
            />
          ))
        : null}
    </div>
  );
}

/** One seat's numbers (SEAT-DRAWER-MODALS: the chart seat panel's Performance modal for its holder). */
export function SeatNumbers({
  api,
  company,
  seat,
  currency,
  today,
  notes = false,
}: {
  api: Api;
  company: CompanyAccess;
  seat: MySeat;
  currency: string;
  today: string;
  /** A note field under every row (the seat panel's Performance modal). */
  notes?: boolean;
}) {
  const label = seatHolderLabel(seat.title, seat.holder_name);
  return (
    <section data-seat={seat.seat_id} aria-label={label} className="flex flex-col gap-2">
      <h2 className="font-heading text-sm font-semibold text-foreground">{label}</h2>
      {seat.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This seat has no number to enter by hand: its KPIs are computed or reported by an agent.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table className="min-w-[44rem]">
            <TableCaption className="sr-only">
              The numbers due for {label}: the period, your value and its status.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Number</TableHead>
                <TableHead scope="col" className="w-56">
                  Period
                </TableHead>
                <TableHead scope="col" className="w-36">
                  Value
                </TableHead>
                <TableHead scope="col" className="w-28">
                  Status
                </TableHead>
                <TableHead scope="col" className="w-20">
                  <span className="sr-only">Save</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {seat.items.map((item) => (
                <NumberRow
                  key={item.metric_def_id}
                  api={api}
                  company={company}
                  seatId={seat.seat_id}
                  item={item}
                  currency={currency}
                  today={today}
                  notes={notes}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function NumberRow({
  api,
  company,
  seatId,
  item,
  currency,
  today,
  notes,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  item: MyNumberItem;
  currency: string;
  today: string;
  notes: boolean;
}) {
  const qc = useQueryClient();
  const draftOf = (key: string) => {
    const v = valueIn(item, key);
    return v === null ? "" : toInputValue(v, item.unit, currency);
  };
  const [period, setPeriod] = useState(item.period_key);
  const [draft, setDraft] = useState(() => draftOf(item.period_key));
  const [reason, setReason] = useState("");
  const [asked, setAsked] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const stored = draft.trim() === "" ? null : toStoredValue(draft, item.unit, currency);
  const correction = isCorrection(item, period, stored);
  const unchanged = stored !== null && valueIn(item, period) === stored;
  const save = useMutation({
    mutationFn: (v: { value: number; note: string | null }) =>
      api.client.observations.create({
        params: { co: company.id },
        body: {
          metric_def_id: item.metric_def_id,
          subject_kind: "seat",
          subject_id: seatId,
          period_type: item.period_type,
          period_key: period,
          value: String(v.value),
          note: v.note,
        },
      }),
    onSuccess: async () => {
      setSaved(period);
      setReason("");
      setAsked(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.myNumbers(company.id) }),
        qc.invalidateQueries({ queryKey: ["company", company.id, "seat", seatId] }),
        // SEAT-DRAWER-MODALS: the org chart's cards and seat panel read the value too.
        qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] }),
      ]);
    },
  });
  const submit = () => {
    if (stored === null || unchanged) return;
    if (correction && !reason.trim()) {
      setAsked(true);
      return;
    }
    save.mutate({ value: stored, note: reason.trim() || null });
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };
  const due = period === item.period_key;
  const chip = due
    ? dueChip(item, today)
    : valueIn(item, period) === null
      ? { label: "Missing", tone: "late" as const }
      : { label: "Entered", tone: "done" as const };
  const inputId = `n-${seatId}-${item.metric_def_id}`;
  const goal = goalOf(item, currency);
  return (
    <>
      <TableRow data-metric={item.key} data-status={due ? item.status : undefined}>
        <TableHead scope="row" className="font-normal">
          <label htmlFor={inputId} className="flex min-w-0 items-center gap-2">
            <HashIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
            <span className="truncate font-medium text-foreground">{item.name}</span>
          </label>
          <span className="block pl-5.5 text-xs text-muted-foreground">
            {goal === "—" ? "No goal set" : `Goal ${goal}`}
            {item.source === "import" ? " · also by CSV import" : ""}
          </span>
        </TableHead>
        <TableCell>
          <NativeSelect
            aria-label={`${item.name}: period`}
            value={period}
            size="sm"
            onChange={(e) => {
              setPeriod(e.target.value);
              setDraft(draftOf(e.target.value));
              setReason("");
              setAsked(false);
              setSaved(null);
            }}
          >
            {item.recent.map((p) => (
              <NativeSelectOption key={p.period_key} value={p.period_key}>
                {periodLabel(item.period_type, p.period_key)}
                {p.value === null ? "" : ` · ${formatMetricValue(p.value, item.unit, currency)}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </TableCell>
        <TableCell>
          <Input
            id={inputId}
            inputMode="decimal"
            className="h-8 tabular-nums"
            value={draft}
            placeholder="—"
            aria-describedby={UNIT_HINT[item.unit] ? `${inputId}-hint` : undefined}
            aria-invalid={draft.trim() !== "" && stored === null ? true : undefined}
            onChange={(e) => {
              setDraft(e.target.value);
              setSaved(null);
            }}
            onKeyDown={onKey}
          />
          {UNIT_HINT[item.unit] ? (
            <span id={`${inputId}-hint`} className="sr-only">
              {UNIT_HINT[item.unit]}
            </span>
          ) : null}
        </TableCell>
        <TableCell>
          {saved === period ? (
            <Badge variant="success">Saved</Badge>
          ) : (
            <Badge variant={TONE[chip.tone]} data-slot="due-chip">
              {chip.label}
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-right">
          <Button
            size="sm"
            variant={correction ? "secondary" : "default"}
            disabled={stored === null || unchanged || save.isPending}
            onClick={submit}
          >
            {correction ? "Correct" : "Save"}
          </Button>
        </TableCell>
      </TableRow>
      {correction && (asked || reason) ? (
        <TableRow data-slot="correction-reason" className="hover:bg-transparent">
          <TableCell colSpan={5} className="pt-0">
            <div className="flex flex-wrap items-center gap-2 pl-5.5">
              <label htmlFor={`${inputId}-why`} className="text-xs text-muted-foreground">
                You are changing {formatMetricValue(valueIn(item, period), item.unit, currency)} to{" "}
                {formatMetricValue(stored, item.unit, currency)}. Why?
              </label>
              <Input
                id={`${inputId}-why`}
                autoFocus
                className="h-8 max-w-md flex-1"
                value={reason}
                placeholder="e.g. two calls were logged late"
                aria-invalid={asked && !reason.trim() ? true : undefined}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={onKey}
              />
              {due ? null : (
                <span className="text-xs text-muted-foreground">
                  Your manager is told about a change to a past period.
                </span>
              )}
            </div>
          </TableCell>
        </TableRow>
      ) : notes ? (
        // SEAT-DRAWER-MODALS: a note with any value (the evidence a reporting duty asks for).
        <TableRow data-slot="entry-note" className="hover:bg-transparent">
          <TableCell colSpan={5} className="pt-0">
            <div className="flex items-center gap-2 pl-5.5">
              <label htmlFor={`${inputId}-note`} className="text-xs text-muted-foreground">
                Note
              </label>
              <Input
                id={`${inputId}-note`}
                className="h-8 max-w-md flex-1"
                value={reason}
                placeholder="Optional"
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={onKey}
              />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
      {save.isError ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5}>
            <FormError what="Saving the number" error={save.error} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/**
 * Home's "Numbers due" card: what is late or due on the seats the caller holds (late first),
 * each opening My numbers. Absent when the caller holds no seat with a number to enter; "all in"
 * when everything due has a value.
 */
export function NumbersDueCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const numbers = useQuery({ ...myNumbersQuery(api, company.id), retry: false });
  const today = localDay(new Date());
  const all = (numbers.data?.seats ?? []).flatMap((s) => s.items);
  if (!numbers.data || all.length === 0) return null;
  const rows = outstanding(numbers.data);
  const late = rows.filter((r) => r.item.status === "late").length;
  return (
    <HomeCard
      title="Numbers due"
      count={rows.length}
      flush
      data-slot="home-numbers-due"
      action={
        <Link
          to="/c/$slug/kpis/my-numbers"
          params={{ slug: company.slug }}
          search={keepFocus}
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          {rows.length ? "Enter numbers" : "My numbers"}
        </Link>
      }
    >
      {rows.length === 0 ? (
        <HomeCardRow
          lead={<CalendarClockIcon aria-hidden="true" className="size-4 text-faint" />}
          title={<span className="text-muted-foreground">All your numbers are in.</span>}
        />
      ) : null}
      {rows.slice(0, 6).map(({ seat, item }) => {
        const chip = dueChip(item, today);
        return (
          <HomeCardRow
            key={`${seat.seat_id}:${item.metric_def_id}`}
            data-metric={item.key}
            lead={<HashIcon aria-hidden="true" className="size-4 text-faint" />}
            title={
              <Link
                to="/c/$slug/kpis/my-numbers"
                params={{ slug: company.slug }}
                search={keepFocus}
                className="truncate font-medium hover:underline"
              >
                {item.name}
              </Link>
            }
            description={`${seatHolderLabel(seat.title, seat.holder_name)} · ${periodLabel(item.period_type, item.period_key)}`}
            trailing={<Badge variant={TONE[chip.tone]}>{chip.label}</Badge>}
          />
        );
      })}
      {rows.length > 6 ? (
        <HomeCardRow
          title={<span className="text-xs text-muted-foreground">and {rows.length - 6} more</span>}
        />
      ) : null}
    </HomeCard>
  );
}
